import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Popper,
  Paper,
  Button,
  Divider,
  ButtonProps,
  PopperProps,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {
  LocalizationProvider,
  DateCalendar,
  DateTimePicker,
  TimePicker,
  DateField,
} from '@mui/x-date-pickers';
import dayjs, {Dayjs} from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import AurIcon, {IconNames} from '../aur-icon/AurIcon';
import {ReactComponent as ExpandIcon} from '../../../assests/svg/down-triangle.svg';
import {t} from 'i18next';
import AurButton from '../aur-button/AurButton';
import CheckIcon from '@mui/icons-material/Check';
import {ReactComponent as ClearIcon} from '../../../assests/svg/clear-Icon-filled.svg';
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {isEmpty} from '../../../utils/utils';
import {AUR_DATE_PICKER_OPTIONS} from '../../../constants/constants';

dayjs.extend(customParseFormat);

const pureDateFormat = ['YYYY/MM/DD', 'YYYY-MM-DD'];
const dateTimeFormats = [
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DD HH:mm:ss.SSS',
  'YYYY/MM/DD HH:mm:ss',
  'YYYY/MM/DD HH:mm:ss.SSS',
];
const timeOnlyFormats = ['HH:mm:ss', 'HH:mm:ss.SSS'];

const conditions = [
  {id: 'Is', label: 'Is'},
  {id: 'Is before or on', label: 'Is before or on'},
  {id: 'Is after or on', label: 'Is after or on'},
  {id: 'Between', label: 'Between'},
];

const filterConditions = [
  {id: 'is', label: 'Is'},
  {id: 'onOrBefore', label: 'Is before or on'},
  {id: 'onOrAfter', label: 'Is after or on'},
  {id: 'between', label: 'Between'},
];

const conditionToTextKey: Record<string, string> = {
  Is: 'common.aurDatePicker.conditions.is',
  'Is before or on': 'common.aurDatePicker.conditions.isBeforeOrOn',
  'Is after or on': 'common.aurDatePicker.conditions.isAfterOrOn',
  Between: 'common.aurDatePicker.conditions.between',
};

type AurDatePickerType =
  | 'date-picker'
  | 'date-range-picker'
  | 'query-date-picker';

export type SelectedDateType = Dayjs | undefined;

export interface AurDatePickerProps {
  type?: AurDatePickerType;
  buttonProps?: Omit<ButtonProps, 'onClick'>;
  popperProps?: PopperProps;
  dateFormat?: string;
  placeholder?: string;
  selectedDate?: SelectedDateType[];
  headerFilter?: boolean;
  showAdvance?: boolean;
  advanceLink?: boolean;
  selectedValue?: string;
  selectedOperator?: string;
  startCalendarProps?: {
    minDate?: Dayjs;
    maxDate?: Dayjs;
  };
  operatorMenuClassName?: string;
  datePickerOption?: string;
  delimiterBetweenOperator?: string;
  onApplyClick?: (selectedDate: SelectedDateType[], operator?: string) => void;
  onCancelClick?: () => void;
  onPopup?: (type: 'open' | 'close') => void;
  clearDates?: boolean;
  enableDateInputForBetween?: boolean;
  shouldShowDateInputWithinPopover?: boolean;
}

const MenuProps = (className?: string, dropdownRef?: any) => {
  return {
    PaperProps: {
      classes: {
        root: className
          ? `aur-conditions-menu-props ${className}`
          : 'aur-conditions-menu-props',
      },
      ref: dropdownRef,
    },
    disableScrollLock: true, // Prevent the scrollbar from disappearing
  };
};

const AurDatePicker: React.FC<AurDatePickerProps> = ({
  type = 'date-picker',
  buttonProps,
  popperProps,
  dateFormat = t('common.yearMonthDate'),
  placeholder = t('common.aurDatePicker.selectDate'),
  selectedDate = [],
  headerFilter = false,
  showAdvance = false,
  advanceLink = true,
  selectedValue = '',
  selectedOperator = '',
  startCalendarProps,
  operatorMenuClassName,
  datePickerOption = AUR_DATE_PICKER_OPTIONS.STRICK_DATE,
  delimiterBetweenOperator = '-',
  onApplyClick,
  onCancelClick,
  onPopup,
  clearDates = false,
  enableDateInputForBetween,
  shouldShowDateInputWithinPopover,
}) => {
  const [selectedStartDate, selectedEndDate] = selectedDate;

  const [condition, setCondition] = useState<string>(
    headerFilter ? 'Is' : 'Between'
  );
  const conditionList = headerFilter ? filterConditions : conditions;
  const [startDate, setStartDate] = useState<Dayjs | undefined>(
    selectedStartDate
  );
  const [endDate, setEndDate] = useState<Dayjs | undefined>(selectedEndDate);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [displayValue, setDisplayValue] = useState<string | undefined>(
    selectedValue
  );
  const popperRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        popperRef.current &&
        !popperRef.current.contains(event.target as Node) &&
        !buttonRef.current?.contains(event.target as Node) &&
        !dropdownRef.current?.contains(event.target as Node)
      ) {
        setAnchorEl(null);
        onPopup?.('close');
      }
    },
    [onPopup]
  );
  const addEventListenerHandler = useCallback(
    () => document.addEventListener('mousedown', handleClickOutside),
    []
  );
  const removeEventListenerHandler = useCallback(
    () => document.removeEventListener('mousedown', handleClickOutside),
    []
  );
  useEffect(() => {
    addEventListenerHandler();
    return () => {
      removeEventListenerHandler();
    };
  }, [addEventListenerHandler, removeEventListenerHandler]);

  useEffect(() => {
    if (popperProps?.open === false) {
      setAnchorEl(null);
    }
  }, [popperProps?.open]);

  useEffect(() => {
    if (headerFilter) {
      if (selectedValue == '') {
        setStartDate(undefined);
        setEndDate(undefined);
        setDisplayValue('');
        setCondition('Is');
      } else {
        const conditionLabel = filterConditions.find(
          (cond) => cond.id == selectedOperator
        )?.label;
        setDisplayValue(selectedValue);
        setCondition(conditionLabel || 'Is');
        if (datePickerOption === AUR_DATE_PICKER_OPTIONS.STRICK_TIME) {
          const currentDate = dayjs().format('YYYY-MM-DD');
          setStartDate(selectedStartDate || dayjs(currentDate + selectedValue));
          if (selectedOperator == 'between') {
            setStartDate(
              selectedStartDate ||
                dayjs(
                  currentDate + selectedValue.split(delimiterBetweenOperator)[0]
                )
            );
            setEndDate(
              selectedEndDate ||
                dayjs(
                  currentDate + selectedValue.split(delimiterBetweenOperator)[1]
                )
            );
          }
        } else {
          setStartDate(selectedStartDate || dayjs(selectedValue));
          if (selectedOperator == 'between') {
            setStartDate(
              selectedStartDate ||
                dayjs(selectedValue.split(delimiterBetweenOperator)[0])
            );
            setEndDate(
              selectedEndDate ||
                dayjs(selectedValue.split(delimiterBetweenOperator)[1])
            );
          }
        }
      }
    }
  }, [headerFilter, selectedValue, selectedOperator]);

  useEffect(() => {
    if (clearDates) {
      setStartDate(undefined);
      setEndDate(undefined);
      setCondition('Between');
    }
  }, [clearDates]);

  const resetDate = useCallback(() => {
    setStartDate(undefined);
    setEndDate(undefined);
    onApplyClick?.([]);
  }, [setStartDate, setEndDate, onApplyClick]);

  const handleConditionChange = useCallback(
    (event: SelectChangeEvent) => {
      setCondition(event.target.value);
      setStartDate(undefined);
      setEndDate(undefined);
    },
    [setCondition]
  );

  const handleStartDateChange = useCallback(
    (date: Dayjs | null) => {
      if (date) setStartDate(date);
    },
    [setStartDate]
  );

  const handleEndDateChange = useCallback(
    (date: Dayjs | null) => {
      if (date) setEndDate(date);
    },
    [setEndDate]
  );

  const handleApply = useCallback(() => {
    onApplyClick?.(
      [startDate, endDate],
      conditionList.find((item) => item.label == condition)?.id
    );
    setDisplayValue(startDate ? startDate.format(dateFormat) : undefined);
    setAnchorEl(null);
    onPopup?.('close');
  }, [
    onApplyClick,
    startDate,
    endDate,
    conditionList,
    dateFormat,
    onPopup,
    condition,
  ]);

  const handleCancel = useCallback(() => {
    if (selectedDate.length === 0) {
      setStartDate(undefined);
      setEndDate(undefined);
    }
    setDisplayValue(undefined);
    onCancelClick?.();
    setAnchorEl(null);
    onPopup?.('close');
  }, [selectedDate.length, onCancelClick, onPopup]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(anchorEl ? null : event.currentTarget);
      onPopup?.('open');
    },
    [anchorEl, onPopup]
  );

<Paper
  className={`${headerFilter ? "filter-date-paper" : "date-paper"}`}
  tabIndex={-1}
  role="dialog"
  aria-modal="false"
>
  ...
</Paper>

  const isQueryDatePicker = useMemo(() => type === 'query-date-picker', [type]);

  const shouldShowRangeCalendar = useMemo(
    () =>
      type === 'date-range-picker' ||
      (isQueryDatePicker && condition === 'Between'),
    [type, isQueryDatePicker, condition]
  );

  const renderPlaceholder = useMemo(() => {
    return (
      <Box className={'aur-date-input-container'}>
        <Typography className="aur-date-input-label date-text" tabIndex={0}>
          {placeholder}
        </Typography>
      </Box>
    );
  }, [placeholder]);

  const renderValue = useMemo(() => {
    const dateValue = shouldShowRangeCalendar
      ? `${selectedStartDate?.format(dateFormat)} ${selectedEndDate ? `- ${selectedEndDate?.format(dateFormat)}` : ''}`
      : `${selectedStartDate?.format(dateFormat)}`;
    return (
      <Box className={'aur-date-input-container'}>
        {isQueryDatePicker && (
          <Typography className="aur-date-input-label date-text-info" tabIndex={0}>
            {condition}:
          </Typography>
        )}
        <Typography className="aur-date-input-label date-text" tabIndex={0}>
          {dateValue}
        </Typography>
      </Box>
    );
  }, [
    shouldShowRangeCalendar,
    selectedStartDate,
    dateFormat,
    selectedEndDate,
    isQueryDatePicker,
    condition,
  ]);

  const renderConditions = useMemo(() => {
    return isQueryDatePicker ? (
      <Select
        fullWidth
        className={'aur-conditions-dropdown-container'}
        value={condition}
        inputProps={{'aria-label': t('indexSearchPage.conditionDropdown')}}
        onChange={handleConditionChange}
        MenuProps={MenuProps(operatorMenuClassName, dropdownRef)}
        tabIndex={0}
        sx={{
          width:
            type === 'date-range-picker' || condition === 'Between'
              ? '47.5%'
              : '100%',
        }}
        renderValue={() => (
          <Typography className="typo-text body3_size ">
            {t(conditionToTextKey[condition])}
          </Typography>
        )}
      >
        {conditionList.map((cond) => (
          <MenuItem key={cond.id} value={cond.label}>
            <Box display="flex" justifyContent="space-between" width="100%" component="aside" aria-label={t('indexSearchPage.condition')}>
              <Typography className="typo-text body3_size ">
                {t(conditionToTextKey[cond.label])}
              </Typography>
              {[condition].includes(cond.label) && (
                <CheckIcon fontSize="small" className="aur-date-check-mark" />
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
    ) : undefined;
  }, [
    isQueryDatePicker,
    condition,
    handleConditionChange,
    type,
    conditionList,
  ]);

  const setLabelForSelectedDates = () => {
    if (datePickerOption === AUR_DATE_PICKER_OPTIONS.STRICK_DATE)
      return [
        t('common.aurDatePicker.startDate'),
        t('common.aurDatePicker.endDate'),
      ];
    else if (datePickerOption === AUR_DATE_PICKER_OPTIONS.DATE_TIME)
      return [
        t('common.aurDatePicker.startDateTime'),
        t('common.aurDatePicker.endDateTime'),
      ];
    else if (datePickerOption === AUR_DATE_PICKER_OPTIONS.STRICK_TIME)
      return [
        t('common.aurDatePicker.startTime'),
        t('common.aurDatePicker.endTime'),
      ];
  };

  const renderDateInputField = useMemo(() => {
    return shouldShowDateInputWithinPopover ? (
      <Box className={'aur-horizontal-date-container'}>
        <Box flex={1}>
          <Box className={'aur-date-container'}>
            <Typography className={'aur-date-placholder'}>
              {t('common.aurDatePicker.date')}
            </Typography>
            <DateField
              variant="standard"
              value={startDate ?? null}
              onChange={handleStartDateChange}
              format={t('common.yearMonthDate')}
              maxDate={endDate}
              InputProps={{
                disableUnderline: true,
              }}
              {...startCalendarProps}
            />
          </Box>
        </Box>
      </Box>
    ) : null;
  }, [shouldShowDateInputWithinPopover, dateFormat, startDate, endDate]);

  const renderDateRangeField = useMemo(() => {
    return shouldShowRangeCalendar ? (
      <Box className={'aur-horizontal-date-container'}>
        <Box flex={1}>
          <Box className={'aur-date-container'}>
            <Typography className={'aur-date-placholder'}>
              {setLabelForSelectedDates()?.[0]}
            </Typography>
            <DateField
              variant="standard"
              value={startDate ?? null}
              onChange={handleStartDateChange}
              format={t('common.yearMonthDate')}
              maxDate={endDate}
              InputProps={{
                disableUnderline: true,
              }}
              {...startCalendarProps}
            />
          </Box>
        </Box>

        <Divider orientation="horizontal" flexItem className="aur-divider" />

        <Box flex={1} justifyContent={'flex-end'}>
          <Box className={'aur-date-container'}>
            <Typography className={'aur-date-placholder'}>
              {setLabelForSelectedDates()?.[1]}
            </Typography>
            <DateField
              variant="standard"
              value={endDate ?? null}
              onChange={handleEndDateChange}
              format={t('common.yearMonthDate')}
              minDate={startDate}
              InputProps={{
                disableUnderline: true,
              }}
            />
          </Box>
        </Box>
      </Box>
    ) : (
      renderDateInputField
    );
  }, [
    shouldShowRangeCalendar,
    dateFormat,
    startDate,
    endDate,
    renderDateInputField,
  ]);

  const renderBetweenEnterDateField = () => {
    return (
      <Box
        data-testid="between-date-field-input"
        className={`aur-date-field-input-picker ${anchorEl ? 'active-field-input' : ''}`}
      >
        <Typography className="typo-text body4_size title-label">
          {placeholder}
        </Typography>
        <DateField
          data-testid="date-input-field"
          variant="standard"
          value={startDate ?? null}
          onChange={(date: Dayjs | null) => {
            if (date) setStartDate(date);
            onApplyClick?.(
              date?.isValid() ? [date] : [],
              conditionList.find((item) => item.label == condition)?.id
            );
          }}
          format={t('common.yearMonthDate')}
          maxDate={startCalendarProps?.maxDate}
          InputProps={{
            disableUnderline: true,
          }}
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            const anchor = event.currentTarget.closest(
              '[data-testid="between-date-field-input"]'
            ) as HTMLElement;
            setAnchorEl(anchor);
            onPopup?.('open');
          }}
          onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
            const anchor = event.currentTarget.closest(
              '[data-testid="between-date-field-input"]'
            ) as HTMLElement;
            setAnchorEl(anchor);
            onPopup?.('open');
          }}
          slotProps={{
            textField: {
              onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Tab') {
                  setAnchorEl(null);
                  onPopup?.('close');
                }
              },
            },
          }}
          {...startCalendarProps}
        />
      </Box>
    );
  };

  const isValidDateInput = (value: string, acceptedFormats: string[]) => {
    if (value.trim() === '') return true;
    return acceptedFormats.some((format) =>
      dayjs(value, format, true).isValid()
    );
  };

  const renderHeaderFilter = () => {
    if (!showAdvance) {
      return (
        <div className="aur-date-picker-button">
          <TextField
            placeholder={t('common.aurDatePicker.filter')}
            size="small"
            fullWidth
            autoComplete="off"
            value={displayValue ? displayValue : ''}
            onChange={(e) => {
              setDisplayValue(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                let inputValue = displayValue ?? '';
                let format = [''];
                if (datePickerOption === AUR_DATE_PICKER_OPTIONS.STRICK_DATE) {
                  format = pureDateFormat;
                } else if (
                  datePickerOption === AUR_DATE_PICKER_OPTIONS.DATE_TIME
                ) {
                  format = dateTimeFormats;
                } else if (
                  datePickerOption === AUR_DATE_PICKER_OPTIONS.STRICK_TIME
                ) {
                  const currentDate = dayjs().format('YYYY-MM-DD');
                  inputValue = `${currentDate} ${inputValue}`;
                  format = timeOnlyFormats.map(
                    (timeFmt) => `YYYY-MM-DD ${timeFmt}`
                  );
                }
                if (isValidDateInput(inputValue, format)) {
                  onApplyClick?.([dayjs(inputValue)], 'is');
                }
              }
            }}
            InputProps={{
              endAdornment: displayValue && (
                <InputAdornment position="end">
                  <ClearIcon
                    data-testid="clear-icon"
                    onClick={(e) => {
                      setDisplayValue(undefined);
                      resetDate();
                      e.stopPropagation();
                    }}
                  />
                </InputAdornment>
              ),
            }}
          />
          <IconButton
            className="button-container"
            aria-label={t('common.aurDatePicker.dropdownButton')}
            ref={buttonRef}
            tabIndex={0}
            onClick={handleClick}
          >
            <ArrowDropDownOutlinedIcon
              transform={anchorEl ? 'rotate(180)' : ''}
              fontSize="medium"
            />
          </IconButton>
        </div>
      );
    }
    return (
      <div className="advance-filter-date">
        <AurButton
          size="Small"
          type="text"
          label={`${t('common.aurDatePicker.advanced')} ${t('common.aurDatePicker.filter')}`}
          classNames="advanced-filter-button"
          onClick={handleClick}
        />
        <IconButton onClick={handleClick} ref={buttonRef} tabIndex={0} aria-haspopup="dialog"
        aria-expanded={Boolean(anchorEl)}>
          <FilterAltIcon fontSize="small" />
        </IconButton>
      </div>
    );
  };

  const renerPickerButton = () => {
    if (enableDateInputForBetween) {
      return renderBetweenEnterDateField();
    }
    if (headerFilter) return renderHeaderFilter();

    return (
      <Button
        ref={buttonRef}
        data-testid={`${placeholder}-button`}
        className="aur-date-picker-button"
        variant="contained"
        startIcon={<AurIcon icon={IconNames.CalendarTodayOutlined} />}
        endIcon={<ExpandIcon transform={anchorEl ? 'rotate(180)' : ''} aria-hidden="true" />}
        onClick={handleClick}
        children={selectedDate.length > 0 ? renderValue : renderPlaceholder}
        disableRipple
        {...buttonProps}
      />
    );
  };
  const pickerSlotProps = {
    textField: {
      className: 'custom-text-field-date-time',
    },
    desktopPaper: {
      className: 'filter-date-time-calendar',
    },
    actionBar: {
      actions: [],
    },
    digitalClockSection: {
      disableAutoFocus: true,
    },
  };
  const pickerTimeSteps = {
    minutes: 1, // Show all minute options (00-59)
    seconds: 1, // Show all second options (00-59)
  };
  const pickerDesktopModeMediaQuery = '@media (min-width: 0px)';

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {renerPickerButton()}
      {/* Popper (Dropdown for Date Selection) */}
      <Popper
        ref={popperRef}
        className={`${headerFilter ? 'filter-date-picker-popper' : 'aur-date-picker-popper'}`}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-start"
        {...popperProps}
      >
        <Paper
          className={`${headerFilter ? 'filter-date-paper' : 'date-paper'}`}
        >
          <Box component="aside" aria-label={t('indexSearchPage.condition')}>
          {renderConditions}
          {renderDateRangeField}
          </Box>
          {datePickerOption === AUR_DATE_PICKER_OPTIONS.STRICK_DATE && (
            // {/* Calendar Picker */}
            <Box
              component="aside"
              aria-label={t('dateRangePicker.datePicker')}
              className={`${headerFilter ? 'filter-date-container' : 'aur-calendar-container'}`}
            >
              <DateCalendar
                value={startDate ?? null}
                onChange={handleStartDateChange}
                className={`${headerFilter ? 'filter-date-calendar' : 'aur-calendar'}`}
                maxDate={endDate}
                {...startCalendarProps}
              />
              {shouldShowRangeCalendar && (
                <DateCalendar
                  value={endDate ?? null}
                  minDate={startDate}
                  onChange={handleEndDateChange}
                  className={`${headerFilter ? 'filter-date-calendar' : 'aur-calendar'}`}
                />
              )}
            </Box>
          )}
          {datePickerOption === AUR_DATE_PICKER_OPTIONS.DATE_TIME && (
            // {/* Date Picker with time */}
            <Box
              component="aside"
              aria-label={t('customField.dateTime')}
              className={`${headerFilter ? 'filter-date-container bottom-space' : 'aur-calendar-container'}`}
            >
              <DateTimePicker
                value={startDate}
                onChange={handleStartDateChange}
                className={`${headerFilter ? 'filter-date-calendar' : 'aur-calendar'}`}
                maxDate={endDate}
                ampm={false}
                onClose={addEventListenerHandler}
                onOpen={() => setTimeout(removeEventListenerHandler, 100)}
                format={dateFormat}
                views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                slotProps={pickerSlotProps}
                timeSteps={pickerTimeSteps}
                desktopModeMediaQuery={pickerDesktopModeMediaQuery}
                {...startCalendarProps}
              />
              {shouldShowRangeCalendar && (
                <DateTimePicker
                  value={endDate}
                  minDate={startDate}
                  onChange={handleEndDateChange}
                  ampm={false}
                  onClose={addEventListenerHandler}
                  onOpen={() => setTimeout(removeEventListenerHandler, 100)}
                  format={dateFormat}
                  views={[
                    'year',
                    'month',
                    'day',
                    'hours',
                    'minutes',
                    'seconds',
                  ]}
                  className={`${headerFilter ? 'filter-date-calendar' : 'aur-calendar'}`}
                  slotProps={pickerSlotProps}
                  timeSteps={pickerTimeSteps}
                  desktopModeMediaQuery={pickerDesktopModeMediaQuery}
                />
              )}
            </Box>
          )}
          {datePickerOption === AUR_DATE_PICKER_OPTIONS.STRICK_TIME && (
            // {/* Time Picker */}
            <Box
              className={`${headerFilter ? ' filter-date-container' : 'aur-calendar-container'}`}
            >
              <TimePicker
                value={startDate}
                onChange={handleStartDateChange}
                className={`${headerFilter ? 'filter-date-calendar' : 'aur-calendar'}`}
                ampm={false}
                onClose={addEventListenerHandler}
                onOpen={() => setTimeout(removeEventListenerHandler, 100)}
                format={dateFormat}
                views={['hours', 'minutes', 'seconds']}
                slotProps={pickerSlotProps}
                timeSteps={pickerTimeSteps}
                desktopModeMediaQuery={pickerDesktopModeMediaQuery}
              />
              {shouldShowRangeCalendar && (
                <TimePicker
                  value={endDate}
                  onChange={handleEndDateChange}
                  className={`${headerFilter ? 'filter-date-calendar' : 'aur-calendar'}`}
                  ampm={false}
                  onClose={addEventListenerHandler}
                  onOpen={() => setTimeout(removeEventListenerHandler, 100)}
                  format={dateFormat}
                  views={['hours', 'minutes', 'seconds']}
                  slotProps={pickerSlotProps}
                  timeSteps={pickerTimeSteps}
                  desktopModeMediaQuery={pickerDesktopModeMediaQuery}
                />
              )}
            </Box>
          )}

          {/* Footer */}
          {advanceLink && (
            <AurButton
              classNames="aur-date-advanced-button"
              endIcon={<AurIcon icon={IconNames.OpenInNewIcon} />}
              type="text"
              size="Small"
              label={t('common.aurDatePicker.advanced')}
              dataTestId="aur-advanced"
            />
          )}
          <Box className={'aur-date-footer'} component="aside" aria-label={t('gaTracking.querybuilder.cancelChanges')}>
            <AurButton
              classNames="aur-date-cancel-button"
              type="text"
              title={t('common.aurDatePicker.cancel')}
              label={t('common.aurDatePicker.cancel')}
              size="Medium"
              onClick={handleCancel}
              dataTestId="Clear-button"
            />
            <AurButton
              classNames="aur-date-apply-button"
              type="continue"
              title={t('common.aurDatePicker.apply')}
              label={t('common.aurDatePicker.apply')}
              size="Medium"
              onClick={handleApply}
              dataTestId="Apply-button"
              disabled={
                shouldShowRangeCalendar
                  ? isEmpty(startDate) ||
                    isEmpty(endDate) ||
                    !startDate?.isValid() ||
                    !endDate?.isValid() ||
                    endDate.isBefore(startDate, 'day')
                  : isEmpty(startDate) || !startDate?.isValid()
              }
            />
          </Box>
        </Paper>
      </Popper>
    </LocalizationProvider>
  );
};

export default AurDatePicker;
